import React, { useEffect, useMemo, useRef, useState } from "react";
import { useChallenges } from "../context/ChallengesContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Trophy, X, Download, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Win-celebration overlay.
 *
 * Fires when ChallengesContext detects a freshly-completed challenge with
 * the current user as winner_id. Shows:
 *   - A pulsing trophy headline
 *   - The duel scoreboard
 *   - A canvas-generated 1080×1920 share card
 *   - Per-platform share buttons (Facebook, Instagram, X) + Save All
 *
 * Per-platform UX nuances:
 *   - FACEBOOK: opens the sharer URL with a pre-filled link + quote.
 *   - X: opens twitter intent with text + url.
 *   - INSTAGRAM: has no web share endpoint. We download the PNG and tell
 *     the user to upload to their story. On mobile, navigator.share with the
 *     image file works and opens the OS share sheet (which includes IG).
 *   - SAVE ALL: downloads the image + copies a caption to clipboard so the
 *     user can paste anywhere.
 */
const APP_URL = "https://gymwithgrizz.app";
const APP_HASHTAG = "#gymwithgrizz";

export default function ChallengeWinCelebration() {
  const { recentWin, clearRecentWin } = useChallenges();
  const { user } = useAuth();
  const canvasRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  const winData = useMemo(() => {
    if (!recentWin || !user) return null;
    const c = recentWin;
    const iAmChallenger = c.challenger_id === user.user_id;
    const myScore = iAmChallenger ? c.challenger_score : c.opponent_score;
    const theirScore = iAmChallenger ? c.opponent_score : c.challenger_score;
    const theirName = iAmChallenger ? c.opponent_name : c.challenger_name;
    const unit = c.challenge_type === "volume" ? "kg" : c.challenge_type === "bodyweight_loss" ? "kg lost" : c.challenge_type === "reps" ? "reps" : "wkts";
    return {
      myScore: Math.round(myScore || 0),
      theirScore: Math.round(theirScore || 0),
      theirName: theirName || "Buddy",
      title: c.title || "Challenge",
      unit,
    };
  }, [recentWin, user]);

  // Generate the PNG share card whenever the overlay opens.
  useEffect(() => {
    if (!recentWin || !winData || !canvasRef.current) {
      setImageUrl(null);
      return;
    }
    setGenerating(true);
    const c = canvasRef.current;
    // Vertical 9:16 format — works for IG Story, FB Story, TikTok, etc.
    c.width = 1080;
    c.height = 1920;
    const ctx = c.getContext("2d");

    // Background — black with red diagonal stripes
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, 1080, 1920);
    ctx.fillStyle = "rgba(59, 130, 246, 0.08)";
    for (let i = -1920; i < 1920; i += 200) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + 1920, 1920);
      ctx.lineWidth = 60;
      ctx.strokeStyle = "rgba(59, 130, 246, 0.05)";
      ctx.stroke();
    }

    // Top badge
    ctx.fillStyle = "#3b82f6";
    ctx.fillRect(0, 0, 1080, 14);
    ctx.fillRect(0, 1906, 1080, 14);

    // Trophy emoji centered
    ctx.font = "bold 360px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("🏆", 540, 480);

    // "I WON" headline
    ctx.fillStyle = "#3b82f6";
    ctx.font = "bold 140px sans-serif";
    ctx.fillText("I WON", 540, 700);

    // Title (challenge)
    ctx.fillStyle = "#ffffff";
    ctx.font = "60px sans-serif";
    ctx.fillText(winData.title.toUpperCase().slice(0, 28), 540, 800);

    // Scoreboard box
    const boxY = 920;
    const boxH = 380;
    ctx.fillStyle = "rgba(255, 255, 255, 0.04)";
    ctx.fillRect(80, boxY, 920, boxH);
    ctx.strokeStyle = "#262626";
    ctx.lineWidth = 4;
    ctx.strokeRect(80, boxY, 920, boxH);

    // Me column
    ctx.fillStyle = "rgba(59, 130, 246, 0.15)";
    ctx.fillRect(80, boxY, 460, boxH);
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText("ME", 310, boxY + 90);
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 180px sans-serif";
    ctx.fillText(String(winData.myScore), 310, boxY + 280);
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText(winData.unit, 310, boxY + 350);

    // Them column
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText(winData.theirName.toUpperCase().slice(0, 14), 770, boxY + 90);
    ctx.fillStyle = "#737373";
    ctx.font = "bold 180px sans-serif";
    ctx.fillText(String(winData.theirScore), 770, boxY + 280);
    ctx.fillStyle = "#a3a3a3";
    ctx.font = "40px sans-serif";
    ctx.fillText(winData.unit, 770, boxY + 350);

    // Branding footer
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 80px sans-serif";
    ctx.fillText("GYM with GRIZZ", 540, 1620);
    ctx.fillStyle = "#3b82f6";
    ctx.font = "44px sans-serif";
    ctx.fillText(APP_URL.replace(/^https?:\/\//, ""), 540, 1700);
    ctx.fillStyle = "#737373";
    ctx.font = "36px sans-serif";
    ctx.fillText(APP_HASHTAG, 540, 1780);

    c.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
      }
      setGenerating(false);
    }, "image/png", 0.92);
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentWin, winData]);

  if (!recentWin || !winData) return null;

  const caption = `Just won "${winData.title}" — ${winData.myScore} ${winData.unit} vs ${winData.theirName}'s ${winData.theirScore}. 💪 #gymwithgrizz`;

  const downloadImage = async (showToast = true) => {
    if (!imageUrl) return null;
    const a = document.createElement("a");
    a.href = imageUrl;
    a.download = `gymwithgrizz-win-${recentWin.challenge_id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    if (showToast) toast.success("Image saved");
    return imageUrl;
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied");
    } catch { /* ignore */ }
  };

  const shareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(APP_URL)}&quote=${encodeURIComponent(caption)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=600");
    toast("Posted to Facebook — paste the image too if it didn't attach", { duration: 5000 });
    downloadImage(false);
  };

  const shareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(APP_URL)}`;
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=400");
    downloadImage(false);
  };

  const shareInstagram = async () => {
    // IG has no web sharing endpoint. On mobile we try navigator.share with
    // the actual file (which surfaces IG in the share sheet); on desktop we
    // download + copy caption.
    if (navigator.share && imageUrl) {
      try {
        const blob = await (await fetch(imageUrl)).blob();
        const file = new File([blob], "gymwithgrizz-win.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: caption, title: "Gym with Grizz win" });
          return;
        }
      } catch { /* user cancelled or unsupported */ }
    }
    await downloadImage(false);
    await copyCaption();
    toast.success("Image saved + caption copied — open Instagram and paste");
  };

  const shareAll = async () => {
    await downloadImage(false);
    await copyCaption();
    toast.success("Image saved + caption copied — share to any app", { duration: 5000 });
  };

  return (
    <div
      data-testid="challenge-win-overlay"
      className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="max-w-md w-full text-center">
        <button
          data-testid="celebration-close"
          onClick={clearRecentWin}
          className="absolute top-4 right-4 text-neutral-500 hover:text-white p-2"
          title="Close"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="animate-bounce text-7xl mb-3">🏆</div>
        <div className="font-display text-5xl text-blue-500 uppercase tracking-tight animate-pulse mb-1">You Won!</div>
        <div className="text-sm uppercase tracking-widest text-neutral-400 mb-6">{winData.title}</div>

        <div className="grid grid-cols-2 divide-x divide-neutral-800 border border-neutral-800 bg-neutral-900/40 mb-6">
          <div className="p-4 bg-blue-500/10">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">You</div>
            <div className="font-display text-3xl text-white">{winData.myScore}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{winData.unit}</div>
          </div>
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 truncate">{winData.theirName}</div>
            <div className="font-display text-3xl text-neutral-400">{winData.theirScore}</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{winData.unit}</div>
          </div>
        </div>

        {/* Generated share card preview */}
        <canvas ref={canvasRef} className="hidden" />
        {generating ? (
          <div className="text-neutral-500 mb-4 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Generating share card…</div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt="Share card preview"
            data-testid="celebration-preview"
            className="block mx-auto w-44 mb-5 border border-neutral-800"
          />
        ) : null}

        <div className="text-xs uppercase tracking-widest text-neutral-400 mb-2 flex items-center justify-center gap-2">
          <Trophy className="w-3 h-3 text-blue-500" /> Share your win
        </div>

        <div className="grid grid-cols-3 gap-2 mb-2">
          <Button
            data-testid="share-facebook"
            onClick={shareFacebook}
            disabled={generating}
            className="bg-[#1877F2] hover:bg-[#0e63d0] rounded-none uppercase tracking-wider font-display h-11"
          >
            Facebook
          </Button>
          <Button
            data-testid="share-instagram"
            onClick={shareInstagram}
            disabled={generating}
            className="bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCB045] hover:opacity-90 rounded-none uppercase tracking-wider font-display h-11"
          >
            Instagram
          </Button>
          <Button
            data-testid="share-x"
            onClick={shareTwitter}
            disabled={generating}
            className="bg-black hover:bg-neutral-900 border border-white rounded-none uppercase tracking-wider font-display h-11"
          >
            X
          </Button>
        </div>
        <Button
          data-testid="share-all"
          onClick={shareAll}
          disabled={generating}
          variant="outline"
          className="w-full bg-transparent border-blue-500 text-blue-500 hover:bg-blue-500/10 rounded-none uppercase tracking-wider font-display h-11 mb-2"
        >
          <Download className="w-4 h-4 mr-1.5" /> Save All — Share Anywhere
        </Button>
        <Button
          data-testid="share-copy-caption"
          onClick={copyCaption}
          variant="ghost"
          size="sm"
          className="w-full text-neutral-400 hover:text-white uppercase tracking-wider font-display h-9"
        >
          <Check className="w-3 h-3 mr-1.5" /> Just copy caption
        </Button>

        <button
          onClick={clearRecentWin}
          data-testid="celebration-dismiss"
          className="mt-6 text-xs uppercase tracking-widest text-neutral-500 hover:text-white"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
